/**
 * Staff Console → Message Audit tab.
 *
 * Cross-user message health: the backend scan (leads worker,
 * /api/admin/message-audit) surfaces every conversation the AI should have
 * answered but hasn't, plus flagged leads, review-queue backlogs, send
 * failures, budget saturation, and broken campaign CTAs — with heuristic
 * per-account suggestions underneath.
 *
 * The one action here is "Requeue replies": the proven 2026-08-30 recovery
 * (clear needs_attention_* + retry state) after which the reconcile
 * re-drive answers automatically within ~5 minutes. The server re-validates
 * every lead before touching it (human takeovers, archived leads, and
 * already-answered threads are skipped and reported), so the button is safe
 * to press on a stale view.
 *
 * Visual idiom follows lifecycle-panel.tsx / links-panel.tsx: summary cards
 * on top, Card+Table sections, inline two-step confirm instead of a modal.
 */

import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  Loader2,
  RefreshCw,
  Wrench,
  Lightbulb,
  Sparkles,
  Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  useMessageAudit,
  useMessageAuditFix,
  useMessageAuditAiSuggest,
  type MessageAuditIssue,
  type MessageAuditSeverity,
  type MessageAuditUnansweredItem,
  type MessageAuditFixResult,
  type MessageAuditAiSuggestResult,
} from './use-admin'

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

const SEVERITY_META: Record<
  MessageAuditSeverity,
  { label: string; icon: typeof AlertTriangle; text: string; bg: string }
> = {
  critical: { label: 'Critical', icon: AlertTriangle, text: 'text-[#F87171]', bg: 'bg-[rgba(248,113,113,0.12)]' },
  warn: { label: 'Warnings', icon: AlertCircle, text: 'text-[#FBBF24]', bg: 'bg-[rgba(251,191,36,0.12)]' },
  info: { label: 'Info', icon: Info, text: 'text-[#8FB4FA]', bg: 'bg-[rgba(59,130,246,0.12)]' },
}

/** 27.4 → "27h", 55.2 → "2.3d" — waiting times read faster in mixed units. */
function fmtHours(h: number): string {
  if (!Number.isFinite(h)) return '—'
  if (h < 48) return `${Math.round(h)}h`
  return `${(h / 24).toFixed(1)}d`
}

function pctLabel(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`
}

function stateBadgeClass(state: string): string {
  if (state === 'flagged') return 'bg-[rgba(248,113,113,0.12)] text-[#F87171]'
  if (state === 'overdue') return 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]'
  return 'bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]' // parked
}

// ---------------------------------------------------------------------------
// Collapsible issue section shell
// ---------------------------------------------------------------------------

function IssueSection({
  issue,
  defaultOpen,
  action,
  children,
}: {
  issue: MessageAuditIssue
  defaultOpen?: boolean
  action?: React.ReactNode
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen ?? issue.severity === 'critical')
  const meta = SEVERITY_META[issue.severity]
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-left"
        >
          <ChevronDown
            className={cn('h-4 w-4 text-[var(--color-silver)] transition-transform', !open && '-rotate-90')}
          />
          <meta.icon className={cn('h-4 w-4', meta.text)} />
          <CardTitle className="text-base text-[var(--color-silver-bright)]">{issue.title}</CardTitle>
          <Badge variant="outline" className={cn('border-none text-xs', meta.bg, meta.text)}>
            {issue.total.toLocaleString()}
          </Badge>
        </button>
        {action}
      </CardHeader>
      {open && (
        <CardContent>
          <p className="mb-3 text-xs text-[var(--color-silver)]">{issue.description}</p>
          {issue.items.length === 0 ? (
            <p className="py-2 text-sm text-[#34D399]">Nothing found — all clear.</p>
          ) : (
            children
          )}
        </CardContent>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Fix button — inline two-step confirm (links-panel idiom), with the
// fixed/skipped result rendered next to it after the mutation settles.
// ---------------------------------------------------------------------------

function RequeueButton({ leadIds, label }: { leadIds: string[]; label: string }) {
  const fix = useMessageAuditFix()
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<MessageAuditFixResult | null>(null)

  if (leadIds.length === 0 && !result) return null
  return (
    <div className="flex items-center gap-2">
      {result && (
        <span className="text-xs text-[var(--color-silver)]">
          <span className="text-[#34D399]">fixed {result.fixed}</span>
          {result.skipped.length > 0 && (
            <span
              className="ml-1 text-[#FBBF24]"
              title={result.skipped.map((s) => `${s.id}: ${s.reason}`).join('\n')}
            >
              / skipped {result.skipped.length}
            </span>
          )}
        </span>
      )}
      {leadIds.length > 0 &&
        (confirming ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={fix.isPending}
              onClick={() =>
                fix.mutate(
                  { lead_ids: leadIds },
                  {
                    onSuccess: (data) => setResult(data),
                    onSettled: () => setConfirming(false),
                  },
                )
              }
            >
              {fix.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm requeue'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            title="Clears needs_attention + retry state; the 5-min re-drive then replies automatically. Human-owned and already-answered leads are skipped server-side."
            onClick={() => setConfirming(true)}
          >
            <Wrench className="mr-1 h-3 w-3" />
            {label}
          </Button>
        ))}
      {fix.isError && (
        <span className="text-xs text-[#F87171]">{(fix.error as Error).message}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Per-issue tables. Each renders the item shape its detector emits; anything
// unrecognised falls back to a JSON dump so a backend change never renders
// an empty section silently.
// ---------------------------------------------------------------------------

function LeadIssueTable({
  items,
  showState,
}: {
  items: Array<Record<string, unknown>>
  showState: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Lead</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Waiting</TableHead>
            {showState && <TableHead>State</TableHead>}
            <TableHead>Reason / detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => {
            const i = it as unknown as MessageAuditUnansweredItem & {
              days_flagged?: number
              fixable?: boolean
            }
            return (
              <TableRow key={i.lead_id}>
                <TableCell>
                  <div className="text-sm font-medium text-[var(--color-silver-bright)]">
                    {i.lead_name ?? '(no name)'}
                  </div>
                  <div className="text-xs text-[var(--color-silver)]">
                    {i.company ?? '—'} · {i.lead_status}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{i.owner_email}</TableCell>
                <TableCell className="whitespace-nowrap text-xs tabular-nums">
                  {i.hours_waiting !== undefined
                    ? fmtHours(i.hours_waiting)
                    : i.days_flagged !== undefined
                      ? `${i.days_flagged}d flagged`
                      : '—'}
                </TableCell>
                {showState && (
                  <TableCell>
                    <Badge variant="outline" className={cn('border-none text-xs', stateBadgeClass(i.state))}>
                      {i.state}
                    </Badge>
                  </TableCell>
                )}
                <TableCell className="text-xs text-[var(--color-silver)]">
                  {i.needs_attention_reason ?? i.classification ?? '—'}
                  {i.last_failure_code && (
                    <span className="ml-1 font-mono text-[#F87171]">{i.last_failure_code}</span>
                  )}
                  {typeof i.reply_attempts === 'number' && i.reply_attempts > 0 && (
                    <span className="ml-1">· {i.reply_attempts} attempts</span>
                  )}
                  {i.fixable === false && (
                    <span className="ml-1 text-[#FBBF24]">(needs human)</span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

function GenericIssueTable({ items }: { items: Array<Record<string, unknown>> }) {
  // Derive columns from the first item; every detector returns homogeneous
  // rows. Values render as text/numbers; nested samples get a count.
  const cols = Object.keys(items[0] ?? {}).filter((k) => k !== 'user_id' && k !== 'settings')
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map((c) => (
              <TableHead key={c} className="whitespace-nowrap text-xs">
                {c.replace(/_/g, ' ')}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row, idx) => (
            <TableRow key={idx}>
              {cols.map((c) => {
                const v = row[c]
                return (
                  <TableCell key={c} className="whitespace-nowrap text-xs">
                    {Array.isArray(v)
                      ? `${v.length} sample${v.length === 1 ? '' : 's'}: ${v
                          .map((s) => (s as { lead_name?: string | null }).lead_name ?? '?')
                          .join(', ')}`
                      : v === null || v === undefined
                        ? '—'
                        : String(v)}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MessageAuditPage() {
  const { data, isLoading, isError, error, refetch, isRefetching } = useMessageAudit()
  const aiSuggest = useMessageAuditAiSuggest()
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const [aiResult, setAiResult] = useState<MessageAuditAiSuggestResult | null>(null)

  // The audit payload itself carries every user that appears in it, so the
  // filter is derived from the data instead of a second /admin/users fetch.
  const userOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of data?.stats.users ?? []) map.set(u.user_id, u.email)
    for (const issue of data?.issues ?? []) {
      for (const it of issue.items) {
        const uid = it.user_id
        const email = it.owner_email
        if (typeof uid === 'string' && typeof email === 'string') map.set(uid, email)
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [data])

  const filterEmail = useMemo(
    () => userOptions.find(([id]) => id === filterUser)?.[1] ?? null,
    [userOptions, filterUser],
  )

  // Apply the user filter to every list that carries an owner. Items without
  // a user_id (nothing today) pass through untouched.
  const issues = useMemo(() => {
    if (!data) return []
    if (!filterUser) return data.issues
    return data.issues.map((issue) => {
      const items = issue.items.filter((it) => {
        if (typeof it.user_id === 'string') return it.user_id === filterUser
        // failed_sends groups have no user_id; keep groups that still have a
        // matching sample so the code-level counts stay visible in context.
        if (Array.isArray(it.samples)) {
          return (it.samples as Array<{ user_id?: string }>).some((s) => s.user_id === filterUser)
        }
        return true
      })
      // stuck_drafts items are (user, status) groups carrying a draft count
      // in `n`; the badge counts drafts, not groups.
      const total = issue.type === 'stuck_drafts'
        ? items.reduce((acc, it) => acc + (Number(it.n) || 0), 0)
        : items.length
      return { ...issue, items, total }
    })
  }, [data, filterUser])

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running message audit…
      </div>
    )
  }
  if (isError) {
    return (
      <div className="p-12 text-center text-sm text-[#F87171]">
        Audit failed: {(error as Error | null)?.message ?? 'unknown error'}
      </div>
    )
  }

  const bySeverity = (sev: MessageAuditSeverity) =>
    issues.filter((i) => i.severity === sev).reduce((acc, i) => acc + i.total, 0)

  const byType = new Map(issues.map((i) => [i.type, i]))
  const unanswered = byType.get('unanswered_inbound')
  const takeover = byType.get('human_takeover_waiting')
  const flagged = byType.get('flagged_leads')
  const stuckDrafts = byType.get('stuck_drafts')
  const failedSends = byType.get('failed_sends')
  const budget = byType.get('budget_saturation')
  const ctaMissing = byType.get('cta_link_missing')

  // Requeue candidates: flagged + overdue only. Parked leads self-heal when
  // their budget/backoff park expires — clearing the park early would just
  // burn retries against the same budget wall.
  const unansweredRequeueIds = (unanswered?.items ?? [])
    .filter((i) => (i as unknown as MessageAuditUnansweredItem).state !== 'parked')
    .map((i) => (i as unknown as MessageAuditUnansweredItem).lead_id)
  // Flagged section: only the reasons the backend marked requeue-safe.
  const flaggedRequeueIds = (flagged?.items ?? [])
    .filter((i) => i.fixable === true)
    .map((i) => String(i.lead_id))

  const suggestions = filterEmail
    ? data.suggestions.filter((s) => s.user_email === filterEmail)
    : data.suggestions
  const userStats = filterEmail
    ? data.stats.users.filter((u) => u.email === filterEmail)
    : data.stats.users
  const campaignStats = filterEmail
    ? data.stats.campaigns.filter((c) => c.owner_email === filterEmail)
    : data.stats.campaigns

  return (
    <div className="space-y-4">
      {/* Severity summary + controls */}
      <div className="flex flex-wrap items-center gap-3">
        {(['critical', 'warn', 'info'] as const).map((sev) => {
          const meta = SEVERITY_META[sev]
          return (
            <Card key={sev} className="flex-1 min-w-[150px]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="eyebrow text-[var(--color-silver)]">{meta.label}</p>
                  <meta.icon className={cn('h-4 w-4', meta.text)} />
                </div>
                <div className={cn('mt-1 text-2xl font-bold tracking-tight', meta.text)}>
                  {bySeverity(sev).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-[var(--color-silver)]" />
          <Select
            value={filterUser ?? '__all__'}
            onValueChange={(v) => setFilterUser(v === '__all__' ? null : v)}
          >
            <SelectTrigger className="h-8 w-64 text-xs">
              <SelectValue placeholder="Filter by user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All users</SelectItem>
              {userOptions.map(([id, email]) => (
                <SelectItem key={id} value={id}>
                  {email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => void refetch()}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)]"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          Re-scan
        </button>
      </div>

      {/* Issue sections */}
      {unanswered && (
        <IssueSection
          issue={unanswered}
          action={
            unanswered.fixable ? (
              <RequeueButton
                leadIds={unansweredRequeueIds}
                label={`Fix — requeue ${unansweredRequeueIds.length} replies`}
              />
            ) : undefined
          }
        >
          <LeadIssueTable items={unanswered.items} showState />
        </IssueSection>
      )}
      {flagged && (
        <IssueSection
          issue={flagged}
          action={
            flaggedRequeueIds.length > 0 ? (
              <RequeueButton
                leadIds={flaggedRequeueIds}
                label={`Fix — requeue ${flaggedRequeueIds.length} replies`}
              />
            ) : undefined
          }
        >
          <LeadIssueTable items={flagged.items} showState={false} />
        </IssueSection>
      )}
      {takeover && (
        <IssueSection issue={takeover}>
          <LeadIssueTable items={takeover.items} showState={false} />
        </IssueSection>
      )}
      {stuckDrafts && (
        <IssueSection issue={stuckDrafts}>
          <GenericIssueTable items={stuckDrafts.items} />
        </IssueSection>
      )}
      {failedSends && (
        <IssueSection issue={failedSends}>
          <GenericIssueTable items={failedSends.items} />
        </IssueSection>
      )}
      {budget && (
        <IssueSection issue={budget}>
          <GenericIssueTable items={budget.items} />
        </IssueSection>
      )}
      {ctaMissing && (
        <IssueSection issue={ctaMissing}>
          <GenericIssueTable items={ctaMissing.items} />
        </IssueSection>
      )}

      {/* Suggestions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
            <Lightbulb className="h-4 w-4 text-[#FBBF24]" />
            Suggestions
            <span className="text-xs font-normal text-[var(--color-silver)]">
              (heuristics over the last 30 days — thresholds in admin-message-audit.ts)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {suggestions.length === 0 ? (
            <p className="py-2 text-sm text-[var(--color-silver)]">
              No suggestions right now — every heuristic is under its threshold.
            </p>
          ) : (
            <div className="space-y-2">
              {suggestions.map((s, idx) => (
                <div
                  key={idx}
                  className="rounded-md bg-[rgba(255,255,255,0.03)] p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className="border-none bg-[rgba(255,255,255,0.06)] text-xs text-[var(--color-silver-bright)]"
                    >
                      {s.user_email}
                    </Badge>
                    <span className="text-sm text-[var(--color-silver-bright)]">{s.suggestion}</span>
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-silver)]">{s.evidence}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-user stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">
            Per-user messaging (30 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Answered</TableHead>
                  <TableHead className="text-right">Median response</TableHead>
                  <TableHead className="text-right">Positive share</TableHead>
                  {data.ai_available && <TableHead className="text-right">AI review</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {userStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-sm text-[var(--color-silver)]">
                      No message activity in the last 30 days.
                    </TableCell>
                  </TableRow>
                )}
                {userStats.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell>
                      <div className="text-sm text-[var(--color-silver-bright)]">{u.email}</div>
                      <div className="text-xs text-[var(--color-silver)]">{u.plan}</div>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{u.sent_30d.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{u.received_30d.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {u.answered_30d.toLocaleString()}
                      {u.received_30d > 0 && (
                        <span className="ml-1 text-xs text-[var(--color-silver)]">
                          ({Math.round((u.answered_30d / u.received_30d) * 100)}%)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {u.median_response_hours === null ? '—' : fmtHours(u.median_response_hours)}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {u.positive_share === null ? '—' : `${Math.round(u.positive_share * 100)}%`}
                    </TableCell>
                    {data.ai_available && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          disabled={aiSuggest.isPending}
                          onClick={() =>
                            aiSuggest.mutate(
                              { user_id: u.user_id },
                              { onSuccess: (res) => setAiResult(res) },
                            )
                          }
                        >
                          {aiSuggest.isPending && aiSuggest.variables?.user_id === u.user_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <Sparkles className="mr-1 h-3 w-3" />
                              Ask AI
                            </>
                          )}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {aiSuggest.isError && (
            <p className="mt-2 text-xs text-[#F87171]">
              AI review failed: {(aiSuggest.error as Error).message}
            </p>
          )}
          {aiResult && (
            <div className="mt-3 rounded-md bg-[rgba(255,255,255,0.03)] p-3">
              <div className="flex items-center gap-2 text-sm text-[var(--color-silver-bright)]">
                <Sparkles className="h-3.5 w-3.5 text-[#34D399]" />
                AI review · {userOptions.find(([id]) => id === aiResult.user_id)?.[1] ?? aiResult.user_id} ·{' '}
                {aiResult.conversations_analyzed} conversations
              </div>
              <p className="mt-2 whitespace-pre-wrap text-xs text-[var(--color-silver)]">
                {aiResult.suggestions_text}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-campaign reply rates */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">
            Active campaign reply rates
            <span className="ml-2 text-xs font-normal text-[var(--color-silver)]">
              (lifetime counters; rate hidden under 30 sends)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Replied</TableHead>
                  <TableHead className="text-right">Reply rate</TableHead>
                  <TableHead className="text-right">Sent (7d)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaignStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-[var(--color-silver)]">
                      No active campaigns.
                    </TableCell>
                  </TableRow>
                )}
                {campaignStats.map((c) => (
                  <TableRow key={c.campaign_id}>
                    <TableCell className="text-sm text-[var(--color-silver-bright)]">{c.name}</TableCell>
                    <TableCell className="text-xs">{c.owner_email}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{c.stats_sent.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{c.stats_replied.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{pctLabel(c.reply_rate)}</TableCell>
                    <TableCell
                      className={cn(
                        'text-right text-sm tabular-nums',
                        c.sent_7d === 0 && 'font-semibold text-[#FBBF24]',
                      )}
                    >
                      {c.sent_7d.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
